<script setup lang="ts">
import {computed, markRaw, nextTick, onBeforeUnmount, ref, shallowRef, watch} from 'vue'
import * as Sentry from '@sentry/vue'
import PageLoadingBar from '@toolify/client/src/modules/ui/components/PageLoadingBar.vue'
import {useApiRequestStore} from '@toolify/client/src/stores/ApiRequestStore/useApiRequestStore'
import AppStoppedAlertComponent from '@toolify/client/src/modules/layout/components/AppStoppedAlertComponent.vue'
import {useI18n} from 'vue-i18n'
import {UserSettingName} from '@toolify/server/src/models/mongoose/UserModel/enum/UserSettingName'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {
  LocaleType,
} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {useAppStore} from '@toolify/client/src/stores/AppStore/useAppStore'
import InitialLoadingScreenComponent from '@toolify/client/src/modules/layout/components/InitialLoadingScreenComponent.vue'
import {useWorkspaceSocketHooks} from '@toolify/client/src/modules/workspace/composables/useWorkspaceSocketHooks'
import NewNotificationComponent from '@toolify/client/src/modules/notifications/components/NewNotificationComponent.vue'
import FloatingNotificationsOverlay from '@toolify/client/src/modules/notifications/components/FloatingNotificationsOverlay.vue'
import LocalMessageLiveRegionsComponent from '@toolify/client/src/modules/notifications/components/LocalMessageLiveRegionsComponent.vue'
import {useFloatingNotificationsSync} from '@toolify/client/src/modules/notifications/composables/useFloatingNotificationsSync'
import {useAlertStore} from '@toolify/client/src/stores/AlertStore/useAlertStore'
import EnvRibbonComponent from '@toolify/client/src/modules/ui/components/EnvRibbonComponent.vue'
import ProductTourOverlayComponent from '@toolify/client/src/modules/productTour/components/ProductTourOverlayComponent.vue'
import {useProvideScaleAnimationContext} from '@toolify/client/src/modules/layout/composables/useScaleAnimationContext'
import {useActiveTransferGuard} from '@toolify/client/src/modules/files/composables/useActiveTransferGuard'
import {Capacitor} from '@capacitor/core'
import {AppReadyPlugin} from '@toolify/client/src/plugins/AppReadyPlugin'
import {useRouteStore} from '@toolify/client/src/stores/RouteStore/useRouteStore'
import {useAppLayoutStore} from '@toolify/client/src/stores/AppLayoutStore/useAppLayoutStore'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {RouteLocationNormalizedLoadedGeneric, useRoute} from 'vue-router'
import CustomRouterView from '../../routes/components/CustomRouterView.vue'
import StackableRouterViewComponent from '../../routes/components/StackableRouterViewComponent.vue'


useWorkspaceSocketHooks()
const {isFloatingNotificationsInSeparateWindow} = useFloatingNotificationsSync()
const appStore = useAppStore()
const apiRequestStore = useApiRequestStore()
const authStore = useAuthStore()

useActiveTransferGuard()
const notificationStore = useAlertStore()
const appLayoutStore = useAppLayoutStore()
const {locale: i18nLocale, t} = useI18n()
const {
  startAnimation: startScaleAnimation,
  endAnimation: endScaleAnimation,
  notifyAnimationEnd: notifyScaleAnimationEnd,
} = useProvideScaleAnimationContext()

const initialLoadingScreenTimeoutPassed = ref(false)
setTimeout(() => {
  initialLoadingScreenTimeoutPassed.value = true
}, 1000)

const hasLoadingFinished = computed(() => {
  return initialLoadingScreenTimeoutPassed.value && appStore.isFirstRouteInitialized
})

const currentRoute = useRoute()
const CONTENT_INTERACTION_LOCK_REPORT_DELAY_MS = 20000
const isContentInteractionLocked = computed(() => {
  return appStore.shouldShowPageLoadingBar || apiRequestStore.isOffline
})
let contentInteractionLockReportTimeout: ReturnType<typeof setTimeout> | null = null

function clearContentInteractionLockReport() {
  if(!contentInteractionLockReportTimeout) {
    return
  }
  clearTimeout(contentInteractionLockReportTimeout)
  contentInteractionLockReportTimeout = null
}

watch(isContentInteractionLocked, (isLocked) => {
  clearContentInteractionLockReport()
  if(!isLocked) {
    return
  }
  contentInteractionLockReportTimeout = setTimeout(() => {
    contentInteractionLockReportTimeout = null
    Sentry.captureMessage('App content interaction lock exceeded the expected navigation time', {
      level: 'warning',
      extra: {
        isPageLoading: appStore.isPageLoading,
        isOffline: apiRequestStore.isOffline,
        routeFullPath: currentRoute.fullPath,
        requestQueueItems: apiRequestStore.requestQueueItems.map(item => ({
          method: item.method,
          path: item.path,
          status: item.status,
        })),
      },
    })
  }, CONTENT_INTERACTION_LOCK_REPORT_DELAY_MS)
})

onBeforeUnmount(() => {
  clearContentInteractionLockReport()
})


const appStoppedAlert = computed(() => {
  return hasLoadingFinished.value
    ? t('general.appOffline', {appName: ENV.AppBrandingName})
    : t('general.appLoadingLongerThanUsual')
})

const currentUser = computed<IUserResponseInstance | null>(() => {
  return authStore.currentEntity?.entityType === EntityType.User
    ? authStore.currentEntity.payload as IUserResponseInstance
    : null
})
const currentUserLocale = computed<LocaleType>(() => {
  return currentUser.value?.settings?.[UserSettingName.Locale]
})
const shouldHideFloatingNotifications = computed<boolean>(() => {
  return Boolean(currentUser.value?.settings?.[UserSettingName.HideFloatingNotifications])
})
watch(currentUserLocale, () => {
  if(!currentUserLocale.value) {
    return
  }
  i18nLocale.value = currentUserLocale.value as string
})

const shouldShowEnvRibbon = ENV.ShouldShowEnvRibbon

function onAfterEnter(element: Element) {
  endScaleAnimation(element)
  notifyScaleAnimationEnd()
}

async function onImageLoad() {
  if (Capacitor.isNativePlatform()) {
    // Retry logic: On fresh install, Capacitor bridge might not be ready yet
    // Retry up to 10 times with increasing delays to ensure the plugin is registered
    const maxRetries = 10
    const baseDelay = 100 // Start with 100ms delay
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await AppReadyPlugin.notifyAppReady()
        return // Success, exit retry loop
      } catch (error) {
        // If this is the last attempt, log the error
        if (attempt === maxRetries - 1) {
          return
        }
        // Wait before retrying, with exponential backoff
        const delay = baseDelay * Math.pow(1.5, attempt)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }
}

async function onImageError() {
  alert('Failed to load the initial screen, but don\'t worry, click OK to continue')
  onImageLoad()
}


const routeStore = useRouteStore()
// Under a record window the base content is already covered by its overlay. Dimming it there would only
// create a stacking context that drops the minimized items bar (part of the base content) under that
// overlay on every navigation inside the window, e.g. when switching records from the bar.
const isBaseContentDimmed = computed(() => {
  return isContentInteractionLocked.value && (apiRequestStore.isOffline || !routeStore.stackableRoutes.length)
})
const committedStackableRoutes = computed(() => routeStore.stackableRoutes as RouteLocationNormalizedLoadedGeneric[])
const leavingTopRoute = shallowRef<RouteLocationNormalizedLoadedGeneric | null>(null)
const leavingLayerInnerVisible = ref(true)

const displayStackableRoutes = computed(() => {
  const committed = committedStackableRoutes.value
  if(leavingTopRoute.value) {
    return [...committed, leavingTopRoute.value]
  }
  return committed
})

watch(
  committedStackableRoutes,
  (newStack, oldStack) => {
    if(!oldStack) {
      return
    }
    if(newStack.length < oldStack.length) {
      const popped = oldStack[oldStack.length - 1] as RouteLocationNormalizedLoadedGeneric
      leavingTopRoute.value = markRaw(popped)
      leavingLayerInnerVisible.value = true
      nextTick(() => {
        leavingLayerInnerVisible.value = false
      })
    } else if(newStack.length > oldStack.length) {
      leavingTopRoute.value = null
      leavingLayerInnerVisible.value = true
    }
  },
  {flush: 'sync'},
)

function isLeavingLayerRoute(stackableRoute: RouteLocationNormalizedLoadedGeneric) {
  return !!leavingTopRoute.value && stackableRoute.fullPath === leavingTopRoute.value.fullPath
}

function getStackableLayerKey(stackableRoute: RouteLocationNormalizedLoadedGeneric, index: number) {
  return `${stackableRoute.name?.toString() ?? stackableRoute.fullPath}-${index}`
}

function getStackableTransitionName(stackableRoute: RouteLocationNormalizedLoadedGeneric) {
  if(!stackableRoute.meta.depth) {
    return undefined
  }
  return stackableRoute.meta.transitionName?.toString()
}

function onStackableAfterEnter(stackableRoute: RouteLocationNormalizedLoadedGeneric, getIsPassive: () => boolean, element: Element) {
  // A passive layer draws nothing (its item shows in the page's sidebar), so the page has nothing to re-measure.
  if(isLeavingLayerRoute(stackableRoute) || getIsPassive()) {
    endScaleAnimation(element)
    return
  }
  onAfterEnter(element)
}

function onStackableLeaveStackLayer(stackableRoute: RouteLocationNormalizedLoadedGeneric, element: Element) {
  endScaleAnimation(element)
  if(leavingTopRoute.value?.fullPath !== stackableRoute.fullPath) {
    return
  }
  leavingTopRoute.value = null
  leavingLayerInnerVisible.value = true
}

</script>

<template>
  <Transition name="loading-screen">
    <InitialLoadingScreenComponent v-if="!hasLoadingFinished" @imageLoad="onImageLoad" @imageError="onImageError"/>
  </Transition>
  <div class="app-content">
    <Transition name="app-stopped-alert">
      <AppStoppedAlertComponent v-if="apiRequestStore.isOffline" :text="appStoppedAlert"/>
    </Transition>
    <StackableRouterViewComponent
      v-for="(stackableRoute, index) in displayStackableRoutes"
      :key="getStackableLayerKey(stackableRoute, index)"
      :route="stackableRoute"
    >
      <template #default="{Component: currentComponent, getIsPassive}">
        <Transition
          :name="getStackableTransitionName(stackableRoute)"
          :appear="!isLeavingLayerRoute(stackableRoute)"
          class="page-transition-position"
          @beforeEnter="startScaleAnimation"
          @enterCancelled="endScaleAnimation"
          @beforeLeave="startScaleAnimation"
          @leaveCancelled="endScaleAnimation"
          @afterEnter="(element: Element) => onStackableAfterEnter(stackableRoute, getIsPassive, element)"
          @afterLeave="(element: Element) => onStackableLeaveStackLayer(stackableRoute, element)"
        >
          <div
            v-if="!isLeavingLayerRoute(stackableRoute) || leavingLayerInnerVisible"
            class="page-transition-position"
            :key="getStackableLayerKey(stackableRoute, index)"
          >
            <Component :is="currentComponent"/>
          </div>
        </Transition>
      </template>
    </StackableRouterViewComponent>

    <div
      class="app-base-content"
      :class="{'is-loading': isBaseContentDimmed}"
    >
      <CustomRouterView v-if="hasLoadingFinished && routeStore.lastNonStackableRoute" v-slot="{Component: currentComponent, route}" :route="routeStore.lastNonStackableRoute as RouteLocationNormalizedLoadedGeneric">
        <Transition
          :name="route.meta.transitionName?.toString()"
          appear
          class="page-transition-position"
          @beforeEnter="startScaleAnimation"
          @enterCancelled="endScaleAnimation"
          @beforeLeave="startScaleAnimation"
          @afterLeave="endScaleAnimation"
          @leaveCancelled="endScaleAnimation"
          @afterEnter="onAfterEnter"
        >
          <Component :is="currentComponent"/>
        </Transition>
      </CustomRouterView>
    </div>
    <EnvRibbonComponent v-if="shouldShowEnvRibbon"/>
    <PageLoadingBar v-if="appStore.shouldShowPageLoadingBar && hasLoadingFinished"/>
    <FloatingNotificationsOverlay
      v-if="hasLoadingFinished"
      :showServerPopups="!isFloatingNotificationsInSeparateWindow && !shouldHideFloatingNotifications"
    />
    <LocalMessageLiveRegionsComponent/>
    <NewNotificationComponent :key="notificationStore.payload.message" v-if="notificationStore.isOpen"/>
    <ProductTourOverlayComponent v-if="hasLoadingFinished"/>
  </div>
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

.app-content {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.app-base-content {
  position: relative;
  width: 100%;
  height: 100%;

  &.is-loading {
    opacity: 0.85;
    transition: all .1s ease-in;
    pointer-events: none;
  }
}

.page-transition-position {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
}

.depth-transition-100-to-200 {
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
  }
  &-enter-from {
    opacity: 0;
    transform: scale(1.2);
  }
  &-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }
}

.depth-transition-500-to-200 {
  &-enter-active {
    transition: all 0.5s ease;
  }
  &-leave-active {
    transition: all 0.25s ease;
  }
  &-enter-from {
    transform: scale(0.8);
    opacity: 0;
  }
  &-leave-to {
    transform: scale(1.1);
    opacity: 0;
  }
}

.depth-transition-200-to-500 {
  &-enter-active {
    transition: all 0.5s ease;
  }
  &-leave-active {
    transition: all 0.25s ease;
  }
  &-enter-from {
    transform: scale(1.2);
    opacity: 0;
  }
  &-leave-to {
    transform: scale(0.9);
    opacity: 0;
  }
}

.depth-transition-200-to-100 {
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
    transition-delay: 0.25s;
  }
  &-enter-from {
    opacity: 0;
    transform: scale(0.85);
  }
  &-leave-to {
    opacity: 0;
    transform: scale(1.1);
  }
}

.depth-transition-100-to-150, .depth-transition-200-to-150 {
  &-enter-active {
    transition: all 0.3s cubic-bezier(0, 0.5, 0, 1);
    top: 0;
    left: 0;
  }
  &-leave-active {
    transition: all 0.3s cubic-bezier(0.25, 0.75, 0.25, 1);
  }
  &-enter-from {
    transform: translateY(100%);
    opacity: 0;
  }
  &-leave-to {
    opacity: 0;
    transform: scale(0.85);
  }
}



.depth-transition-150-to-100, .depth-transition-150-to-200 {
  &-enter-active {
    transition: all 0.3s cubic-bezier(0.25, 0.75, 0.25, 1);
    transition-delay: .1s;
  }
  &-leave-active {
    transition: all 0.3s cubic-bezier(0.25, 0.75, 0.25, 1);
    top: 0;
    left: 0;
  }
  &-enter-from {
    opacity: 0;
    transform: scale(0.9);
    border-radius: map-get(variables.$radiuses, radius-lg);
    overflow: hidden;
  }
  &-leave-to {
    transform: translateY(50%);
    opacity: 0;
  }
}

.initial-transition {
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
    transition-delay: 0.25s;
  }
  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }
}

.loading-screen {
  &-enter-active,
  &-leave-active {
    transition: all 0.5s ease;
  }
  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: scale(1.5);
  }
}

.app-stopped-alert {

  &-enter-active {
    transition: all .2s cubic-bezier(0, 0.5, 0, 1.5) .1s;
  }
  &-leave-active {
    transition: all .2s cubic-bezier(0.25, 0.75, 0.25, 1);
  }

  &-enter-active,
  &-leave-active {
    transform: translateY(0);
  }
  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(100%);
  }
}
</style>
